// @ts-nocheck
/**
 * Adapter from a project's config + engine to the names the original single-file board used
 * (PEOPLE, P, PODS, DISC_LABEL, PRI_META, ...), so the ported UI reads close to the original.
 */
import type { Engine, ProjectConfig } from "@super-gantt/engine";
import { displayName, fullName } from "@super-gantt/core";

export const WS_COLORS = ["#F6D9BC", "#C6D8EE", "#D6E4C2", "#D8CFE8", "#EFC9C7", "#C3E2EC", "#F5E9B4", "#C8E0BC", "#BCD5DA", "#E3C4D4", "#F3D6B0", "#CFC7E4", "#BFD9F0", "#EFC0C0", "#DBB4AC", "#B4D7A4", "#F5DF94", "#A9C8CD", "#D8AEC2", "#BBAED8", "#C3D4F4", "#D3D0D0", "#F1C79A", "#B7DBC9"];
const DOW_ALL = ["M", "T", "W", "Th", "F", "Sa", "Su"];

export const fmt = (dt: Date) => dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const slug = (s: string) => (s || "project").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "project";
export const baseIdOf = (id: string) => id.split("~")[0];

export function buildModel(config: ProjectConfig, engine: Engine) {
  const PEOPLE = config.resources.map(r => ({
    name: r.key, label: displayName(r, config), full: fullName(r), disc: r.discipline, fte: r.fte, offset: r.offsetWeeks || 0, blockLabel: r.blockLabel || "", userId: r.userId || null,
  }));
  const P = Object.fromEntries(PEOPLE.map(p => [p.name, p]));
  // members that are not configured resources would crash the row renderer; skip them
  const PODS = config.teams.map((t, idx) => ({ ...t, idx, members: t.members.filter(m => P[m]) }));
  const PERSON_PODS = engine.personTeams;
  const DISC_LABEL = Object.fromEntries(config.disciplines.map(d => [d.key, d.label]));
  const PRI_META = Object.fromEntries(config.priorities.map((p, i) => [p.key, { label: p.label, color: p.color, idx: i }]));
  const PRI = Object.fromEntries(config.priorities.map((p, i) => [p.key, i]));
  const DPW = config.workdaysPerWeek;
  const MW = config.milestoneWeeks || 4;
  const START = new Date(config.startDate + "T00:00:00");
  const RUNWAY_W = config.runwayWeeks;
  const DOW = Array.from({ length: DPW }, (_, i) => DOW_ALL[i] ?? String(i + 1));
  const defU = (nm: string) => engine.defaultUtil(nm);
  const priName = (p: string) => PRI_META[p]?.label || p;
  const discLabel = (d: string) => DISC_LABEL[d] ?? d;
  const labelOf = (nm: string) => P[nm]?.label ?? nm;
  const wsOwner = (w: string) => engine.workstreamOwner(w);
  return {
    PEOPLE, P, PODS, PERSON_PODS, DISC_LABEL, PRI_META, PRI, DPW, MW, START, RUNWAY_W, DOW,
    defU, priName, discLabel, labelOf, wsOwner,
    rate: engine.rate, laneOf: engine.laneOf, parseIntended: engine.parseIntended, pinTracks: engine.pinTracks,
    simPerson: engine.simPerson, computeMilestones: engine.computeMilestones, dayToDate: engine.dayToDate,
  };
}
export type BoardModel = ReturnType<typeof buildModel>;

/** Jira-style priority icons, shaped by the priority's position in the config (blocker, critical, major, minor, trivial). */
export function PriIcon({ p, size = 10, model }: { p: string; size?: number; model: BoardModel }) {
  const meta = model.PRI_META[p];
  const c = meta?.color || "#8993A4";
  const i = meta ? meta.idx : 4;
  const s = { width: size, height: size, flexShrink: 0, display: "inline-block", verticalAlign: "-1px" };
  if (i === 0) return <svg style={s} viewBox="0 0 14 14"><circle cx="7" cy="7" r="6.4" fill={c} /><rect x="3" y="6" width="8" height="2" rx="1" fill="#fff" /></svg>;
  if (i === 1) return <svg style={s} viewBox="0 0 14 14"><path d="M2 8.5 L7 3.5 L12 8.5" stroke={c} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 12 L7 7 L12 12" stroke={c} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (i === 2) return <svg style={s} viewBox="0 0 14 14"><path d="M2 10 L7 4 L12 10" stroke={c} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (i === 3) return <svg style={s} viewBox="0 0 14 14"><path d="M2 4 L7 10 L12 4" stroke={c} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  return <svg style={s} viewBox="0 0 14 14"><path d="M2 3 L7 7.5 L12 3" stroke={c} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 7.5 L7 12 L12 7.5" stroke={c} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
